import { useCallback, useState } from 'react';

import { SubmitContactFieldsInput, ValidationItem } from '@app/queryTyping';

import { useNotifierContext } from '@app/common/components/Notifier';
import { serializeResult } from '@app/common/configurable-wizards/utils';

import { isTriggeredEventCanceledApolloError } from '@app/core/components/TriggeredEvents/TriggeredEventCanceledApolloError';

import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import {
  useSubmitUserContactFieldsMutation,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/submitUserContactFields';
import { AddressFormCache } from '@app/widgets/contacts-and-settings/types';

export type OnErrorProps = {
  readonly errors: ValidationItem[];
  readonly values: AddressFormCache;
};

export type UseSubmitContact = {
  readonly successMessage?: string;
  readonly onSuccess?: () => void;
  readonly onError?: (props: OnErrorProps) => void;
};

export const useSubmitContact = ({ successMessage, onSuccess, onError }: UseSubmitContact) => {
  const [submitFields, { loading: submitLoading, error }] = useSubmitUserContactFieldsMutation();
  const { settingsQuery } = useSettingFieldsContext();
  const { addAlerts, clearAlerts } = useNotifierContext();
  const [refetchLoading, setRefetchLoading] = useState(false);

  const refetchSettings = useCallback(async () => {
    setRefetchLoading(true);

    await settingsQuery.refetch();

    if (successMessage) {
      clearAlerts();
      addAlerts([{
        level: 'success',
        title: successMessage,
      }]);
    }

    setRefetchLoading(false);

    if (onSuccess) {
      onSuccess();
    }
  }, []);

  const submitContact = useCallback(async (values: AddressFormCache) => {
    await submitFields({
      variables: {
        data: {
          values: serializeResult(values) as SubmitContactFieldsInput['values'],
        },
      },
      async update(_, { data }) {
        const { errors } = data?.submitUserContactFields || {};

        if (errors?.length) {
          if (onError) {
            onError({ errors, values });
          }

          return;
        }

        await refetchSettings();
      },
    });
  }, []);

  if (error && !isTriggeredEventCanceledApolloError(error)) {
    throw error;
  }

  return {
    isLoading: submitLoading || refetchLoading,
    submitContact,
  };
};
